import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useAuthStatus } from '../../utils'
import { useUser, useTours, useSelectedTours } from '../../hooks'
import { areDatesOverlapping, formatDateToYYYYMMDD } from '../../utils/date'
import { Calendar } from '../Calendar'
import styles from './TourSelection.module.scss'

const TourRegion = {
	DOMESTIC: 'DOMESTIC',
	INTERNATIONAL: 'INTERNATIONAL',
}

const Gender = {
	MALE: 'MALE',
	FEMALE: 'FEMALE',
}

// 통화 기호 매핑
const CURRENCY_SYMBOLS = {
	KRW: '₩',
	USD: '$',
	EUR: '€',
	JPY: '¥',
	GBP: '£',
}

// 총상금 포맷팅 함수
const formatPurse = (purse, currency = 'KRW') => {
	const formattedAmount = purse.toLocaleString('ko-KR')

	if (currency === 'KRW') {
		return `총상금 ${formattedAmount}원`
	}

	const symbol = CURRENCY_SYMBOLS[currency] || '$'
	return `총상금 ${symbol}${formattedAmount}(${currency})`
}

export const TourSelection = ({
	TourCount,
	selectOnlyOne,
	onSelectedToursChange,
	onNoToursAvailable,
	isOverseasIncluded,
	previousSelectedTours = [],
	mode = 'default', // 'default' 또는 'offer'
	gender: externalGender, // 선택적으로 외부에서 성별을 강제 지정 (FEMALE/MALE)
	upcomingOnly = false, // true면 시작일이 오늘 이후인 대회만 표시 (출전예정 대회 설정용)
	disabledTourIds = [], // 상대방이 이미 계약 중인 대회 ID (선택 불가)
	filterYear = null, // 공고 작성 등: 해당 연도 대회만 목록에 표시 (null이면 연도 필터 없음)
}) => {
	const [selectedTourIds, setSelectedTourIds] = useState([])
	const [selectedTourCount, setSelectedTourCount] = useState('')
	const [selectedRegion, setSelectedRegion] = useState(TourRegion.DOMESTIC)
	const [selectedTourCategory, setSelectedTourCategory] = useState(null)
	const [currentView, setCurrentView] = useState('list') // 'list' 또는 'calendar'
	const [calendarDate, setCalendarDate] = useState(new Date().toISOString().split('T')[0]) // 달력에서 보고 있는 날짜

	const [auth] = useAuthStatus()
	const { data: user } = useUser()

	// gender 결정: 외부에서 주어진 값 우선, 없으면 user.tourPro.tour_type 기반
	const selectedGender = useMemo(() => {
		if (externalGender === Gender.MALE || externalGender === Gender.FEMALE) {
			return externalGender
		}
		if (!user?.tourPro?.tour_type) return null
		return user.tourPro.tour_type === 'KLPGA' ? Gender.FEMALE : Gender.MALE
	}, [externalGender, user?.tourPro?.tour_type])

	const { data: tours, isLoading: isToursLoading } = useTours(selectedGender)
	const { data: userSelectedTourIds } = useSelectedTours(auth.user?.id)

	console.log('tours', tours)

	// 전체 tours 데이터에서 ID로 투어 찾기
	const findTourById = useCallback(
		(tourId) => {
			if (!tours) return null

			// 국내 투어에서 찾기
			const domesticTour = tours.domestic?.find((t) => t.id === tourId)
			if (domesticTour) return domesticTour

			// 해외 투어에서 찾기
			if (tours.international?.tours) {
				const allInternationalTours = Object.values(tours.international.tours).flat()
				return allInternationalTours.find((t) => t.id === tourId)
			}

			return null
		},
		[tours],
	)

	// 선택된 투어들과 대상 투어의 일정이 겹치는지 확인
	const isTourOverlappingWithSelected = useCallback((targetTour, selectedTours) => {
		if (!selectedTours || selectedTours.length === 0) {
			return false
		}

		// 대상 투어에 날짜 정보가 없으면 겹침 계산 불가 → 겹치지 않는 것으로 처리
		if (!targetTour || !targetTour.started_at || !targetTour.ended_at) {
			return false
		}

		for (const selected of selectedTours) {
			// 선택된 투어의 날짜 정보가 없으면 스킵
			if (!selected || !selected.started_at || !selected.ended_at) continue
			if (
				areDatesOverlapping(
					selected.started_at,
					selected.ended_at,
					targetTour.started_at,
					targetTour.ended_at,
				)
			) {
				return true
			}
		}
		return false
	}, [])

	// 현재 표시할 투어 목록 (region과 category에 따라, upcomingOnly면 오늘 이후 대회만)
	const tourList = useMemo(() => {
		if (!tours) return []

		let filteredTours = []

		if (selectedRegion === TourRegion.DOMESTIC) {
			// 국내 투어
			filteredTours = tours.domestic || []
		} else {
			// 해외 투어
			if (selectedTourCategory) {
				// 특정 카테고리 선택된 경우 - 해당 카테고리의 데이터만 (없으면 빈 배열)
				filteredTours = tours.international?.tours?.[selectedTourCategory] || []
			} else if (tours.international?.tours) {
				// 카테고리 선택 안된 경우 전체 해외 투어
				filteredTours = Object.values(tours.international.tours).flat()
			}
		}

		// 출전예정 대회 설정 시: 시작일이 오늘 이후인 대회만 표시 (지난 대회 제외)
		if (upcomingOnly && filteredTours.length > 0) {
			const todayStart = new Date()
			todayStart.setHours(0, 0, 0, 0)
			filteredTours = filteredTours.filter((t) => {
				const start = t.started_at ? new Date(t.started_at) : null
				return start && start >= todayStart
			})
		}

		if (filterYear != null) {
			filteredTours = filteredTours.filter((t) => {
				if (!t.started_at) return false
				return new Date(t.started_at).getFullYear() === filterYear
			})
		}

		return filteredTours
	}, [tours, selectedRegion, selectedTourCategory, upcomingOnly, filterYear])

	const handleCheckboxToggle = (tourId) => {
		if (disabledTourIds.includes(tourId)) return
		const toggledTour = tourList.find((tour) => tour.id === tourId)

		if (!toggledTour) return

		let newSelectedIds

		if (selectOnlyOne) {
			// 하나만 선택 가능
			newSelectedIds = selectedTourIds.includes(tourId) ? [] : [tourId]
		} else {
			if (selectedTourIds.includes(tourId)) {
				// 이미 선택된 투어 제거
				newSelectedIds = selectedTourIds.filter((id) => id !== tourId)
			} else {
				// 전체 투어 데이터에서 이미 선택된 투어들 가져오기 (국내/해외 모두 포함)
				const currentlySelectedTours = selectedTourIds
					.map((id) => findTourById(id))
					.filter((tour) => tour !== null)

				// 일정 겹침 체크
				if (isTourOverlappingWithSelected(toggledTour, currentlySelectedTours)) {
					console.log('이 투어는 이미 선택된 다른 투어와 기간이 겹칩니다.')
					return
				}
				// 새로운 투어 추가
				newSelectedIds = [...selectedTourIds, tourId]
			}
		}

		// 날짜 순 정렬 (시작일 오름차순, 날짜 없으면 뒤로)
		const sortedIds = [...newSelectedIds].sort((a, b) => {
			const ta = findTourById(a)
			const tb = findTourById(b)
			const sa = ta?.started_at ? new Date(ta.started_at).getTime() : Infinity
			const sb = tb?.started_at ? new Date(tb.started_at).getTime() : Infinity
			return sa - sb
		})

		setSelectedTourIds(sortedIds)

		if (onSelectedToursChange && typeof onSelectedToursChange === 'function') {
			onSelectedToursChange(sortedIds)
		}
	}

	useEffect(() => {
		if (
			previousSelectedTours &&
			previousSelectedTours.tours &&
			previousSelectedTours.tours.length > 0
		) {
			let toursToSelect = previousSelectedTours.tours
			if (upcomingOnly) {
				const todayStart = new Date()
				todayStart.setHours(0, 0, 0, 0)
				toursToSelect = previousSelectedTours.tours.filter((tour) => {
					const startDate = tour.started_at ?? tour.startedAt
					const start = startDate ? new Date(startDate) : null
					return start && start >= todayStart
				})
			}
			const tourIds = toursToSelect.map((tour) => tour.id)
			const normalizedTourIds = selectOnlyOne && tourIds.length > 1 ? [tourIds[0]] : tourIds
			setSelectedTourIds(normalizedTourIds)
			if (
				selectOnlyOne &&
				tourIds.length > 1 &&
				onSelectedToursChange &&
				typeof onSelectedToursChange === 'function'
			) {
				onSelectedToursChange(normalizedTourIds)
			}
		}
	}, [previousSelectedTours, upcomingOnly, selectOnlyOne, onSelectedToursChange])

	// 단기 계약(selectOnlyOne)에서는 어떤 경로로 들어오든 선택값을 1개로 보정
	useEffect(() => {
		if (!selectOnlyOne || selectedTourIds.length <= 1) return
		const normalizedTourIds = [selectedTourIds[0]]
		setSelectedTourIds(normalizedTourIds)
		if (onSelectedToursChange && typeof onSelectedToursChange === 'function') {
			onSelectedToursChange(normalizedTourIds)
		}
	}, [selectOnlyOne, selectedTourIds, onSelectedToursChange])

	useEffect(() => {
		setSelectedTourCount(selectedTourIds.length)
	}, [selectedTourIds])

	const handleRegionChange = (region) => {
		setSelectedRegion(region)
		setSelectedTourCategory(null)
	}

	const handleTourCategoryClick = (categoryString) => {
		setSelectedTourCategory((prevCategory) =>
			prevCategory === categoryString ? null : categoryString,
		)
	}

	// 달력용 이벤트 변환
	const calendarEvents = useMemo(() => {
		return tourList.map((tour) => {
			const end = new Date(tour.ended_at)
			end.setDate(end.getDate() + 1)
			const isSelected = selectedTourIds.includes(tour.id)

			// 전체 투어 데이터에서 이미 선택된 투어들 가져오기
			const currentlySelectedTours = selectedTourIds
				.map((id) => findTourById(id))
				.filter((t) => t !== null)

			// 날짜 겹침 체크
			const isOverlapping =
				selectedTourIds.length > 0 &&
				!selectedTourIds.includes(tour.id) &&
				isTourOverlappingWithSelected(tour, currentlySelectedTours)
			const isDisabledByPartner = disabledTourIds.includes(tour.id)
			const isDisabled =
				isDisabledByPartner ||
				(selectOnlyOne && selectedTourIds.length > 0 && !isSelected) ||
				isOverlapping

			// 색상 결정
			let backgroundColor, borderColor
			if (isSelected) {
				backgroundColor = '#085e4b' // 선택: 진한 그린
				borderColor = '#085e4b'
			} else if (isDisabled) {
				backgroundColor = 'rgba(8, 94, 75, 0.2)' // 비활성: 진한 그린 20% 투명도
				borderColor = 'rgba(8, 94, 75, 0.2)'
			} else {
				backgroundColor = '#d6d6d6' // 미선택: 회색
				borderColor = '#d6d6d6'
			}

			return {
				id: tour.id,
				title: tour.name,
				start: tour.started_at,
				end: end.toISOString().split('T')[0],
				allDay: true,
				backgroundColor,
				borderColor,
				classNames: isDisabled ? ['disabled-event'] : [],
			}
		})
	}, [
		tourList,
		selectedTourIds,
		findTourById,
		isTourOverlappingWithSelected,
		selectOnlyOne,
		disabledTourIds,
	])

	const hasAnyTourMatchingFilterYear = useMemo(() => {
		if (!tours || filterYear == null) return true
		const inYear = (t) => t.started_at && new Date(t.started_at).getFullYear() === filterYear
		if ((tours.domestic || []).some(inYear)) return true
		if (tours.international?.tours) {
			return Object.values(tours.international.tours).flat().some(inYear)
		}
		return false
	}, [tours, filterYear])

	useEffect(() => {
		if (!onNoToursAvailable || filterYear == null) return
		onNoToursAvailable(!hasAnyTourMatchingFilterYear)
	}, [onNoToursAvailable, filterYear, hasAnyTourMatchingFilterYear])

	// 국내/해외 선택된 대회 개수 계산
	const domesticCount = useMemo(() => {
		if (!tours) return 0
		return selectedTourIds.filter((id) => {
			// 전체 tours 데이터에서 찾기
			const domesticTour = tours.domestic?.find((t) => t.id === id)
			return !!domesticTour
		}).length
	}, [selectedTourIds, tours])

	const internationalCount = useMemo(() => {
		if (!tours) return 0
		return selectedTourIds.filter((id) => {
			// 전체 해외 투어에서 찾기
			if (!tours.international?.tours) return false
			return Object.values(tours.international.tours)
				.flat()
				.some((t) => t.id === id)
		}).length
	}, [selectedTourIds, tours])

	const tourCategoryList = useMemo(() => {
		// 1) 서버에서 국제 대회 카테고리 제공 시 우선 사용
		const serverCategories = tours?.international?.categories
		if (Array.isArray(serverCategories) && serverCategories.length > 0) {
			return serverCategories.map((cat) => ({ name: cat, value: cat }))
		}

		// 2) 없으면 기존 하드코딩 유지
		if (selectedGender === Gender.MALE) {
			return [
				{ name: 'PGA', value: 'PGA' },
				{ name: 'ASIAN', value: 'ASIAN' },
				{ name: 'DP', value: 'DP' },
				{ name: 'JGTO', value: 'JGTO' },
				{ name: 'KORNFERRY', value: 'KORNFERRY' },
			]
		} else {
			return [
				{ name: 'KLPGA', value: 'KLPGA' },
				{ name: 'LPGA', value: 'LPGA' },
				{ name: 'JLPGA', value: 'JLPGA' },
				{ name: 'LET', value: 'LET' },
			]
		}
	}, [tours?.international?.categories, selectedGender])

	// 해외 탭일 때 카테고리 기본 선택: 목록이 있고 아직 선택이 없다면 첫 번째 항목 자동 선택
	useEffect(() => {
		if (
			selectedRegion === TourRegion.INTERNATIONAL &&
			(!selectedTourCategory ||
				!tourCategoryList.some((c) => c.value === selectedTourCategory)) &&
			tourCategoryList.length > 0
		) {
			setSelectedTourCategory(tourCategoryList[0].value)
		}
	}, [selectedRegion, selectedTourCategory, tourCategoryList])

	// 투어 데이터가 로드되거나 바뀔 때도 날짜 기준으로 정렬 유지
	useEffect(() => {
		if (!selectedTourIds || selectedTourIds.length === 0) return
		const sorted = [...selectedTourIds].sort((a, b) => {
			const ta = findTourById(a)
			const tb = findTourById(b)
			const sa = ta?.started_at ? new Date(ta.started_at).getTime() : Infinity
			const sb = tb?.started_at ? new Date(tb.started_at).getTime() : Infinity
			return sa - sb
		})
		if (sorted.some((v, i) => v !== selectedTourIds[i])) {
			setSelectedTourIds(sorted)
			if (onSelectedToursChange && typeof onSelectedToursChange === 'function') {
				onSelectedToursChange(sorted)
			}
		}
	}, [tours, findTourById])

	// 로딩 중일 때
	if (isToursLoading) {
		return <div className={styles['tour-list']}>투어 목록 로딩 중...</div>
	}

	return (
		<div className={styles['tour-list']}>
			<div className={styles['select-domestic-overseas']}>
				{mode === 'offer' && (
					<div className={styles['offer-mode-message']}>
						대회를 선택해주세요{' '}
						{selectOnlyOne ? (
							<>
								(<span>{selectedTourCount}</span>/1)
							</>
						) : (
							<>
								(총 <span>{selectedTourCount}</span>경기)
							</>
						)}
					</div>
				)}
				<div className={styles['domestic-or-overseas']}>
					<div
						className={`${styles.domestic} ${selectedRegion === TourRegion.DOMESTIC ? styles.active : ''}`}
						onClick={() => handleRegionChange(TourRegion.DOMESTIC)}>
						국내 {mode === 'default' ? domesticCount : ''}
					</div>
					{/* {isOverseasIncluded === true && ( */}
					<div
						className={`${styles.overseas} ${selectedRegion === TourRegion.INTERNATIONAL ? styles.active : ''}`}
						onClick={() => handleRegionChange(TourRegion.INTERNATIONAL)}>
						해외 {mode === 'default' ? internationalCount : ''}
					</div>
					{/* )} */}
				</div>
			</div>

			<div className={styles['view-container']}>
				{selectedRegion === TourRegion.INTERNATIONAL && (
					<div className={styles['tour-category-container']}>
						{tourCategoryList.map((category) => (
							<div
								key={category.value}
								className={`${styles['tour-category-item']} ${selectedTourCategory === category.value ? styles.active : ''}`}
								onClick={() => handleTourCategoryClick(category.value)}>
								{category.name}
							</div>
						))}
					</div>
				)}

				{/* 리스트/달력 보기 토글 버튼 */}
				<div className={styles['watch-method']}>
					<div className={styles['view-toggle']}>
						<div
							className={`${styles['watch-content-list']} ${currentView === 'list' ? styles['active-view'] : ''}`}
							onClick={() => setCurrentView('list')}>
							<svg
								width="12"
								height="8"
								viewBox="0 0 12 8"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M0.53125 0.9375C0.53125 0.575063 0.825063 0.28125 1.1875 0.28125C1.54994 0.28125 1.84375 0.575063 1.84375 0.9375C1.84375 1.29994 1.54994 1.59375 1.1875 1.59375C0.825063 1.59375 0.53125 1.29994 0.53125 0.9375ZM3.375 0.9375C3.375 0.695875 3.57088 0.5 3.8125 0.5H10.8125C11.0541 0.5 11.25 0.695875 11.25 0.9375C11.25 1.17912 11.0541 1.375 10.8125 1.375H3.8125C3.57088 1.375 3.375 1.17912 3.375 0.9375ZM0.53125 4C0.53125 3.63756 0.825063 3.34375 1.1875 3.34375C1.54994 3.34375 1.84375 3.63756 1.84375 4C1.84375 4.36244 1.54994 4.65625 1.1875 4.65625C0.825063 4.65625 0.53125 4.36244 0.53125 4ZM3.375 4C3.375 3.75838 3.57088 3.5625 3.8125 3.5625H10.8125C11.0541 3.5625 11.25 3.75838 11.25 4C11.25 4.24162 11.0541 4.4375 10.8125 4.4375H3.8125C3.57088 4.4375 3.375 4.24162 3.375 4ZM0.53125 7.0625C0.53125 6.70006 0.825063 6.40625 1.1875 6.40625C1.54994 6.40625 1.84375 6.70006 1.84375 7.0625C1.84375 7.42494 1.54994 7.71875 1.1875 7.71875C0.825063 7.71875 0.53125 7.42494 0.53125 7.0625ZM3.375 7.0625C3.375 6.82088 3.57088 6.625 3.8125 6.625H10.8125C11.0541 6.625 11.25 6.82088 11.25 7.0625C11.25 7.30412 11.0541 7.5 10.8125 7.5H3.8125C3.57088 7.5 3.375 7.30412 3.375 7.0625Z"
									fill="#4B4A48"
								/>
							</svg>
							목록으로 보기
						</div>
						<div
							className={`${styles['watch-content-calendar']} ${currentView === 'calendar' ? styles['active-view'] : ''}`}
							onClick={() => setCurrentView('calendar')}>
							<svg
								width="12"
								height="12"
								viewBox="0 0 12 12"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<path
									d="M6.4375 6.4375C6.4375 6.67912 6.24162 6.875 6 6.875C5.75838 6.875 5.5625 6.67912 5.5625 6.4375C5.5625 6.19588 5.75838 6 6 6C6.24162 6 6.4375 6.19588 6.4375 6.4375Z"
									fill="#4B4A48"
								/>
								<path
									d="M3.375 8.1875C3.61662 8.1875 3.8125 7.99162 3.8125 7.75C3.8125 7.50838 3.61662 7.3125 3.375 7.3125C3.13338 7.3125 2.9375 7.50838 2.9375 7.75C2.9375 7.99162 3.13338 8.1875 3.375 8.1875Z"
									fill="#4B4A48"
								/>
								<path
									d="M3.8125 9.0625C3.8125 9.30412 3.61662 9.5 3.375 9.5C3.13338 9.5 2.9375 9.30412 2.9375 9.0625C2.9375 8.82088 3.13338 8.625 3.375 8.625C3.61662 8.625 3.8125 8.82088 3.8125 9.0625Z"
									fill="#4B4A48"
								/>
								<path
									d="M4.6875 8.1875C4.92912 8.1875 5.125 7.99162 5.125 7.75C5.125 7.50838 4.92912 7.3125 4.6875 7.3125C4.44588 7.3125 4.25 7.50838 4.25 7.75C4.25 7.99162 4.44588 8.1875 4.6875 8.1875Z"
									fill="#4B4A48"
								/>
								<path
									d="M5.125 9.0625C5.125 9.30412 4.92912 9.5 4.6875 9.5C4.44588 9.5 4.25 9.30412 4.25 9.0625C4.25 8.82088 4.44588 8.625 4.6875 8.625C4.92912 8.625 5.125 8.82088 5.125 9.0625Z"
									fill="#4B4A48"
								/>
								<path
									d="M6 8.1875C6.24162 8.1875 6.4375 7.99162 6.4375 7.75C6.4375 7.50838 6.24162 7.3125 6 7.3125C5.75838 7.3125 5.5625 7.50838 5.5625 7.75C5.5625 7.99162 5.75838 8.1875 6 8.1875Z"
									fill="#4B4A48"
								/>
								<path
									d="M6.4375 9.0625C6.4375 9.30412 6.24162 9.5 6 9.5C5.75838 9.5 5.5625 9.30412 5.5625 9.0625C5.5625 8.82088 5.75838 8.625 6 8.625C6.24162 8.625 6.4375 8.82088 6.4375 9.0625Z"
									fill="#4B4A48"
								/>
								<path
									d="M7.3125 8.1875C7.55412 8.1875 7.75 7.99162 7.75 7.75C7.75 7.50838 7.55412 7.3125 7.3125 7.3125C7.07088 7.3125 6.875 7.50838 6.875 7.75C6.875 7.99162 7.07088 8.1875 7.3125 8.1875Z"
									fill="#4B4A48"
								/>
								<path
									d="M7.75 9.0625C7.75 9.30412 7.55412 9.5 7.3125 9.5C7.07088 9.5 6.875 9.30412 6.875 9.0625C6.875 8.82088 7.07088 8.625 7.3125 8.625C7.55412 8.625 7.75 8.82088 7.75 9.0625Z"
									fill="#4B4A48"
								/>
								<path
									d="M8.625 8.1875C8.86662 8.1875 9.0625 7.99162 9.0625 7.75C9.0625 7.50838 8.86662 7.3125 8.625 7.3125C8.38338 7.3125 8.1875 7.50838 8.1875 7.75C8.1875 7.99162 8.38338 8.1875 8.625 8.1875Z"
									fill="#4B4A48"
								/>
								<path
									d="M7.75 6.4375C7.75 6.67912 7.55412 6.875 7.3125 6.875C7.07088 6.875 6.875 6.67912 6.875 6.4375C6.875 6.19588 7.07088 6 7.3125 6C7.55412 6 7.75 6.19588 7.75 6.4375Z"
									fill="#4B4A48"
								/>
								<path
									d="M8.625 6.875C8.86662 6.875 9.0625 6.67912 9.0625 6.4375C9.0625 6.19588 8.86662 6 8.625 6C8.38338 6 8.1875 6.19588 8.1875 6.4375C8.1875 6.67912 8.38338 6.875 8.625 6.875Z"
									fill="#4B4A48"
								/>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M2.9375 0.3125C3.17912 0.3125 3.375 0.508375 3.375 0.75V1.625H8.625V0.75C8.625 0.508375 8.82088 0.3125 9.0625 0.3125C9.30412 0.3125 9.5 0.508375 9.5 0.75V1.625H9.9375C10.904 1.625 11.6875 2.4085 11.6875 3.375V9.9375C11.6875 10.904 10.904 11.6875 9.9375 11.6875H2.0625C1.096 11.6875 0.3125 10.904 0.3125 9.9375V3.375C0.3125 2.4085 1.096 1.625 2.0625 1.625H2.5V0.75C2.5 0.508375 2.69588 0.3125 2.9375 0.3125ZM10.8125 5.5625C10.8125 5.07925 10.4207 4.6875 9.9375 4.6875H2.0625C1.57925 4.6875 1.1875 5.07925 1.1875 5.5625V9.9375C1.1875 10.4207 1.57925 10.8125 2.0625 10.8125H9.9375C10.4207 10.8125 10.8125 10.4207 10.8125 9.9375V5.5625Z"
									fill="#4B4A48"
								/>
							</svg>
							달력으로 보기
						</div>
					</div>
				</div>

				{currentView === 'list' ? (
					<div
						className={`${styles['tour-list-container']} ${tourList.length === 0 ? styles['no-tours-background'] : ''}`}>
						{tourList.length > 0 ? (
							tourList.map((tour) => {
								// 전체 투어 데이터에서 이미 선택된 투어들 가져오기 (국내/해외 모두 포함)
								const currentlySelectedTours = selectedTourIds
									.map((id) => findTourById(id))
									.filter((t) => t !== null)

								const isOverlapping =
									selectedTourIds.length > 0 &&
									!selectedTourIds.includes(tour.id) &&
									isTourOverlappingWithSelected(tour, currentlySelectedTours)
								const isCheckboxDisabled =
									disabledTourIds.includes(tour.id) ||
									(selectOnlyOne &&
										selectedTourIds.length > 0 &&
										!selectedTourIds.includes(tour.id)) ||
									isOverlapping

								return (
									<div
										key={tour.id}
										className={`${styles['tour-item']} ${styles['custom-checkbox-container']} ${isCheckboxDisabled ? styles['disabled-tour-item'] : ''}`}
										onClick={() =>
											!isCheckboxDisabled && handleCheckboxToggle(tour.id)
										}>
										<input
											type="checkbox"
											id={`tour-${tour.id}`}
											checked={selectedTourIds.includes(tour.id)}
											readOnly
											className={styles['tour-checkbox']}
											disabled={isCheckboxDisabled}
										/>
										<div className={styles['tour-details']}>
											<div className={styles['tour-date-container']}>
												<div className={styles['date-bar']}></div>
												<div className={styles['tour-dates']}>
													{formatDateToYYYYMMDD(tour.started_at)} ~{' '}
													{formatDateToYYYYMMDD(tour.ended_at)}
												</div>
											</div>
											<div className={styles['tour-name']}>{tour.name}</div>
											<div className={styles['tour-purse']}>
												{formatPurse(tour.purse, tour.currency)}
											</div>
										</div>
									</div>
								)
							})
						) : (
							<div className={styles['no-tours-message']}>
								현재 예정된 대회 목록이 없어요.<br></br>추후 대회 일정이 업데이트
								되면, 알려드릴게요!
							</div>
						)}
					</div>
				) : (
					<div className={styles['calendar-container']}>
						<Calendar
							initialDate={calendarDate}
							events={calendarEvents}
							onClickEvent={(info) => {
								// disabled 이벤트는 클릭 무시
								if (info.event.classNames.includes('disabled-event')) {
									return
								}
								// 이벤트 클릭 시 해당 투어 선택/해제
								const tourId = info.event.id
								handleCheckboxToggle(tourId)
							}}
							onDatesSet={(dateInfo) => {
								// 달력에서 보고 있는 날짜 저장 (월 이동 시에도 유지)
								setCalendarDate(dateInfo.start.toISOString().split('T')[0])
							}}
						/>
					</div>
				)}
			</div>
		</div>
	)
}
